// src/screens/PartnerAnalytics.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "../styles/PartnerAnalyticsDemo.css";
import StatBox from "../components/StatBox";
import GuestCard from "../components/GuestCard";
import TrafficChart from "../components/TrafficChart";

/**
 * ✅ IMPORTANT:
 * - do NOT include /api in REACT_APP_API_URL
 * - in Capacitor, default to deployed API if env isn't present
 * (matches PartnerHomeScreen.js behavior)
 */
const API_BASE = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const envBase = clean(process.env.REACT_APP_API_URL);
  if (envBase) return envBase;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor =
    origin.startsWith("capacitor://") ||
    origin.includes("localhost") ||
    origin.includes("127.0.0.1");

  if (isCapacitor) return "https://pay2pee.onrender.com";
  return origin;
})();

const EMPTY_TRAFFIC = {
  Monday: 0,
  Tuesday: 0,
  Wednesday: 0,
  Thursday: 0,
  Friday: 0,
  Saturday: 0,
  Sunday: 0,
};

const PartnerAnalytics = () => {
  useEffect(() => {
    document.body.classList.add("p2p-fullwidth");
    return () => document.body.classList.remove("p2p-fullwidth");
  }, []);

  const [stats, setStats] = useState({ activeGuests: 0, totalGuestsToday: 0 });
  const [requestedGuests, setRequestedGuests] = useState([]);
  const [activeGuests, setActiveGuests] = useState([]);
  const [weeklyTraffic, setWeeklyTraffic] = useState(EMPTY_TRAFFIC);

  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(true);
  const [graceSecondsRemaining, setGraceSecondsRemaining] = useState(null);
  const [locationId, setLocationId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // tick so countdowns update live
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  // =========================
  // ✅ ANALYTICS HELPERS
  // =========================
  const track = (eventName, params = {}) => {
    try {
      if (typeof window === "undefined") return;
      if (typeof window.gtag !== "function") return;

      window.gtag("event", eventName, {
        ...params,
        app: "pay2pee",
        screen: "partner_analytics",
      });
    } catch {
      // no-op
    }
  };

  // SPA page_view (Partner Analytics)
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (typeof window.gtag !== "function") return;

      window.gtag("event", "page_view", {
        page_title: "Partner Analytics",
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.hash,
      });
    } catch {
      // no-op
    }
  }, []);

  const toMillis = (ts) => {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (typeof ts === "string") {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
    if (ts?._seconds) return ts._seconds * 1000;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    return null;
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const normalizeWeeklyTraffic = (liveTrafficRaw) => {
    let normalizedTraffic = {};
    if (Array.isArray(liveTrafficRaw)) {
      normalizedTraffic = liveTrafficRaw.reduce((acc, item) => {
        if (!item || !item.day) return acc;
        const value =
          typeof item.value === "number"
            ? item.value
            : typeof item.count === "number"
            ? item.count
            : 0;
        acc[item.day] = value;
        return acc;
      }, {});
    } else if (liveTrafficRaw && typeof liveTrafficRaw === "object") {
      normalizedTraffic = liveTrafficRaw;
    }

    const map = {
      Mon: "Monday",
      Tue: "Tuesday",
      Wed: "Wednesday",
      Thu: "Thursday",
      Fri: "Friday",
      Sat: "Saturday",
      Sun: "Sunday",
    };

    const out = { ...EMPTY_TRAFFIC };
    Object.keys(normalizedTraffic || {}).forEach((k) => {
      const key = map[k] || k;
      if (out[key] !== undefined) out[key] = normalizedTraffic[k];
    });

    return out;
  };

  // ✅ NEW: normalize guests so GuestCard can show name + avatar with no GuestCard changes
  // GuestCard expects:
  // - guest.name
  // - guest.avatar (NOT avatarUrl)
  const normalizeGuestForCard = (g) => {
    if (!g || typeof g !== "object") return g;

    const displayName =
      g.guestName ||
      g.guestDisplayName ||
      g.userName ||
      g.name ||
      (g.userId ? `Guest ${String(g.userId).slice(-4)}` : "Guest");

    const avatar =
      g.guestAvatarUrl ||
      g.guestPhotoUrl ||
      g.userAvatarUrl ||
      g.avatar ||
      g.avatarUrl ||
      g.photoUrl ||
      "";

    return {
      ...g,
      name: displayName,
      avatar, // ✅ IMPORTANT: GuestCard uses guest.avatar
    };
  };

  const fetchAnalytics = useRef(async () => {});
  fetchAnalytics.current = async () => {
    setErr(null);

    const t0 = Date.now();
    try {
      const res = await axios.get(`${API_BASE}/api/partner/analytics`, {
        headers: authHeader,
      });

      const data = res.data || {};
      const liveStats = data.stats || {};

      // ✅ only change: normalize incoming guests for name/avatar
      const liveRequestedRaw = Array.isArray(data.requestedGuests)
        ? data.requestedGuests
        : [];
      const liveActiveRaw = Array.isArray(data.activeGuests)
        ? data.activeGuests
        : [];

      const liveRequested = liveRequestedRaw.map(normalizeGuestForCard);
      const liveActive = liveActiveRaw.map(normalizeGuestForCard);

      const liveTrafficRaw = data.weeklyTraffic || [];

      setLocationId(data.locationId || null);

      const liveAutoAccept =
        data.autoAcceptEnabled ?? data.autoAcceptGuests ?? data.autoAccept ?? true;
      setAutoAcceptEnabled(!!liveAutoAccept);

      if (
        data.graceSecondsRemaining !== undefined &&
        data.graceSecondsRemaining !== null
      ) {
        setGraceSecondsRemaining(Number(data.graceSecondsRemaining));
      } else {
        setGraceSecondsRemaining(null);
      }

      const nextStats = {
        activeGuests:
          liveStats.activeGuests != null ? liveStats.activeGuests : liveActive.length,
        totalGuestsToday:
          liveStats.totalGuestsToday != null
            ? liveStats.totalGuestsToday
            : liveRequested.length + liveActive.length,
      };

      setStats(nextStats);
      setRequestedGuests(liveRequested);
      setActiveGuests(liveActive);
      setWeeklyTraffic(normalizeWeeklyTraffic(liveTrafficRaw));

      if (loading) {
        track("partner_analytics_loaded", {
          ms: Date.now() - t0,
          location_id_present: !!data.locationId,
          auto_accept: !!liveAutoAccept,
          grace_seconds:
            data.graceSecondsRemaining != null ? Number(data.graceSecondsRemaining) : null,
          active_guests: Number(nextStats.activeGuests || 0),
          requested_guests: Number(liveRequested.length || 0),
          total_today: Number(nextStats.totalGuestsToday || 0),
        });
      }
    } catch (error) {
      console.error("Failed to load partner analytics:", error);
      setErr("Could not load live analytics.");

      track("partner_analytics_failed", {
        status: error?.response?.status || 0,
        message: String(error?.message || error),
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load + realtime-ish polling
  useEffect(() => {
    fetchAnalytics.current();
    const interval = setInterval(() => fetchAnalytics.current(), 3000);
    return () => clearInterval(interval);
  }, []);

  // ✅ analytics: track meaningful KPI changes (avoids spamming every poll)
  const lastKpiRef = useRef({
    active: null,
    requested: null,
    total: null,
    grace: null,
    auto: null,
  });

  useEffect(() => {
    if (loading) return;

    const active = Number(activeGuests.length || 0);
    const requested = Number(requestedGuests.length || 0);
    const total = Number(stats.totalGuestsToday || active + requested);
    const grace = graceSecondsRemaining == null ? null : Number(graceSecondsRemaining);
    const auto = !!autoAcceptEnabled;

    const prev = lastKpiRef.current;

    const changed =
      prev.active !== active ||
      prev.requested !== requested ||
      prev.total !== total ||
      prev.grace !== grace ||
      prev.auto !== auto;

    if (changed) {
      track("partner_analytics_kpi_update", {
        active_guests: active,
        requested_guests: requested,
        total_today: total,
        grace_seconds: grace,
        auto_accept: auto,
      });

      lastKpiRef.current = { active, requested, total, grace, auto };
    }
  }, [
    loading,
    activeGuests,
    requestedGuests,
    stats.totalGuestsToday,
    graceSecondsRemaining,
    autoAcceptEnabled,
  ]);

  // Grace countdown ticker (UI only)
  useEffect(() => {
    if (graceSecondsRemaining === null) return;
    const t = setInterval(() => {
      setGraceSecondsRemaining((s) => (s === null ? null : s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [graceSecondsRemaining]);

  const handleToggleAutoAccept = async (checked) => {
    track("partner_auto_accept_toggle_click", { checked: !!checked });

    setAutoAcceptEnabled(checked);

    try {
      if (!locationId) throw new Error("Missing locationId (analytics did not return it).");

      await axios.put(
        `${API_BASE}/api/host/auto-accept`,
        { locationId, autoAcceptGuests: checked },
        { headers: authHeader }
      );

      track("partner_auto_accept_toggle_success", { checked: !!checked });

      await fetchAnalytics.current();
    } catch (error) {
      console.error("Error toggling auto-accept:", error);

      track("partner_auto_accept_toggle_failed", {
        checked: !!checked,
        status: error?.response?.status || 0,
        message: String(error?.message || error),
      });

      alert("Could not update auto-accept. Please try again.");
      setAutoAcceptEnabled((v) => !v);
    }
  };

  const handleAcceptGuest = async (guestId) => {
    const idStr = guestId.toString();

    track("partner_guest_accept_click", { guest_id: idStr });

    try {
      await axios.post(
        `${API_BASE}/api/partner/guests/${idStr}/accept`,
        {},
        { headers: authHeader }
      );

      track("partner_guest_accept_success", { guest_id: idStr });

      await fetchAnalytics.current();
    } catch (error) {
      console.error("Error accepting guest:", error);

      track("partner_guest_accept_failed", {
        guest_id: idStr,
        status: error?.response?.status || 0,
        message: String(error?.message || error),
      });

      alert("There was a problem accepting this guest. Please try again.");
    }
  };

  const handleEndVisit = async (guestId) => {
    const idStr = guestId.toString();

    track("partner_guest_end_click", { guest_id: idStr });

    try {
      await axios.post(
        `${API_BASE}/api/partner/guests/${idStr}/end`,
        {},
        { headers: authHeader }
      );

      track("partner_guest_end_success", { guest_id: idStr });

      await fetchAnalytics.current();
    } catch (error) {
      console.error("Error ending visit:", error);

      track("partner_guest_end_failed", {
        guest_id: idStr,
        status: error?.response?.status || 0,
        message: String(error?.message || error),
      });

      alert("There was a problem ending this visit. Please try again.");
    }
  };

  const nowMs = Date.now();

  const activeGuestsSorted = [...activeGuests]
    .sort((a, b) => (toMillis(a.startTime) || 0) - (toMillis(b.startTime) || 0))
    .map((g) => {
      const expiresMs = toMillis(g.expiresAt);
      const remaining = expiresMs
        ? Math.max(0, Math.floor((expiresMs - nowMs) / 1000))
        : null;
      return { ...g, _remainingSeconds: remaining };
    });

  const requestedGuestsSorted = [...requestedGuests].sort(
    (a, b) => (toMillis(a.createdAt) || 0) - (toMillis(b.createdAt) || 0)
  );

  const activeCount = activeGuestsSorted.length;
  const totalCount =
    stats.totalGuestsToday || activeCount + requestedGuestsSorted.length;

  if (loading) {
    return (
      <div className="dashboard">
        <h2>Loading analytics…</h2>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {err && <div className="demo-banner">{err}</div>}

      {graceSecondsRemaining !== null && graceSecondsRemaining > 0 ? (
        <div className="grace-banner">
          Cleaning / prep time: <strong>{formatTime(graceSecondsRemaining)}</strong>
        </div>
      ) : null}

      <div className="card">
        <div className="analytics-header">
          <h2>Partner Analytics</h2>

          <label className="auto-accept-toggle global-toggle">
            <span>Auto-accept new guests</span>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                className="toggle-input"
                checked={autoAcceptEnabled}
                onChange={(e) => handleToggleAutoAccept(e.target.checked)}
              />
              <span className="toggle-slider" />
            </div>
          </label>
        </div>

        <div className="stats-container">
          <StatBox title="Active Guests" value={activeCount} />
          <StatBox title="Total Guests Today" value={totalCount} />
        </div>

        <h2>Requested Guests</h2>
        <div className="guest-list">
          {requestedGuestsSorted.length === 0 && (
            <p className="empty-state">No pending guest requests right now.</p>
          )}

          {requestedGuestsSorted.map((guest) => {
            const id = (guest.id || guest._id || "").toString();
            return (
              <div key={id} className="guest-row">
                <GuestCard guest={guest} />
                <button className="accept-btn" onClick={() => handleAcceptGuest(id)}>
                  Accept
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h2>Active Guests</h2>
        <div className="guest-list">
          {activeGuestsSorted.length === 0 && (
            <p className="empty-state">
              No guests are currently active. Accepted guests will appear here.
            </p>
          )}

          {activeGuestsSorted.map((guest) => {
            const id = (guest.id || guest._id || "").toString();
            return (
              <div key={id} className="guest-row">
                <GuestCard guest={guest} />
                <button className="end-btn" onClick={() => handleEndVisit(id)}>
                  End Visit
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card full-width">
        <h2>Weekly Foot Traffic</h2>
        <TrafficChart data={weeklyTraffic} />
      </div>
    </div>
  );
};

export default PartnerAnalytics;